import { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { History, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { DataTablePagination } from "./data-table-pagination";
import {
  useGetValidationHistoryQuery,
  type ValidationHistoryEvent,
} from "@/services/conciliation/dtef-validation";

const COLUMNS: ColumnDef<ValidationHistoryEvent>[] = [
  {
    accessorKey: "created_at",
    header: "Data/Hora",
    cell: ({ row }) => {
      try {
        return format(new Date(row.original.created_at), "dd/MM/yyyy HH:mm");
      } catch {
        return row.original.created_at;
      }
    },
  },
  {
    accessorKey: "email",
    header: "Usuário",
  },
  {
    id: "rowCount",
    header: "Registros",
    cell: ({ row }) =>
      row.original.event_payload?.metadata?.rowCount?.toLocaleString("pt-BR") ?? "-",
  },
  {
    id: "found",
    header: "Encontrados",
    cell: ({ row }) => {
      const found = row.original.event_payload?.metadata?.found;
      if (found == null) return "-";
      return (
        <span className="text-green-600 font-medium">
          {found.toLocaleString("pt-BR")}
        </span>
      );
    },
  },
  {
    id: "notFound",
    header: "Não Encontrados",
    cell: ({ row }) => {
      const notFound = row.original.event_payload?.metadata?.notFound;
      if (notFound == null) return "-";
      return (
        <span className={notFound > 0 ? "text-red-500 font-medium" : "text-gray-500"}>
          {notFound.toLocaleString("pt-BR")}
        </span>
      );
    },
  },
];

export const ValidationHistoryTable = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data: historyData, isLoading, refetch, isFetching } =
    useGetValidationHistoryQuery(
      {
        page,
        limit: pageSize,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      },
      { refetchOnMountOrArgChange: true },
    );

  const tableData = historyData?.data ?? [];
  const totalPages = historyData?.pagination?.totalPages ?? 1;
  const totalRows = historyData?.pagination?.total ?? 0;

  const columns = useMemo(() => COLUMNS, []);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    rowCount: totalRows,
    state: {
      pagination: { pageIndex: page - 1, pageSize },
    },
    onPaginationChange: (updater) => {
      const prev = { pageIndex: page - 1, pageSize };
      const next = typeof updater === "function" ? updater(prev) : updater;
      const sizeChanged = next.pageSize !== prev.pageSize;
      setPage(sizeChanged ? 1 : next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
  });

  return (
    <Card className="w-full mt-6">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-[#613F4F]" />
              <CardTitle className="text-lg text-[#613F4F]">
                Histórico de Validações
              </CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Atualizar histórico"
            >
              <RefreshCw
                className={`h-4 w-4 text-[#613F4F] ${isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 mt-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">De</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-[160px] h-9 border-[#613F4F]/20 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-[#613F4F]">Até</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-[160px] h-9 border-[#613F4F]/20 text-sm"
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
              className="text-xs text-[#613F4F]"
            >
              Limpar filtro
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <TableHead key={h.id}>
                      {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : tableData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Nenhuma validação encontrada.
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4">
          <DataTablePagination table={table} />
        </div>
      </CardContent>
    </Card>
  );
};
